import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from "../services/auth.service";
import { inject } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { UserInfoService } from '../services/user.info.service';
import { OnboardingQuiz, OnboardingService } from '../services/onboarding.service';

function stripAccessCode(url: string): string {
  const u = new URL(url, window.location.origin);
  u.searchParams.delete('accessCode');
  return u.pathname + (u.search ? u.search : '') + (u.hash || '');
}

function toOnboardingQuiz(obj: any): OnboardingQuiz | null {
  if (!obj) return null;

  if (Array.isArray(obj) && obj.length) {
    obj = obj[0];
  }

  const { flow, heightUnit, heightValue, weightUnit, weightValue } = obj;
  if (
    typeof flow === 'string' &&
    typeof heightUnit === 'string' &&
    typeof weightUnit === 'string' &&
    typeof heightValue === 'number' &&
    typeof weightValue === 'number'
  ) {
    return { flow, heightUnit, heightValue, weightUnit, weightValue };
  }
  return null;
}

function decodeQuizDataParam(raw: string | null): unknown[] | null {
  if (!raw) return null;

  let val = raw.trim();

  const tryDecode = (s: string) => {
    try {
      const json = atob(s);
      return JSON.parse(json);
    } catch { return null; }
  };

  let parsed = tryDecode(val);
  if (parsed) return parsed;

  const urlSafe = val.replace(/-/g, '+').replace(/_/g, '/');

  const pad = urlSafe + '='.repeat((4 - (urlSafe.length % 4)) % 4);

  parsed = tryDecode(pad);
  if (parsed) return parsed;

  const plusFixed = pad.replace(/ /g, '+');
  parsed = tryDecode(plusFixed);
  return parsed;
}

export const authGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const userInfoService = inject(UserInfoService); 
  const onboardingService = inject(OnboardingService); 

  const refreshToken = localStorage.getItem(AuthService.FIREBASE_REFRESH_TOKEN);
  const accessCode = route.queryParamMap.get('accessCode');
  const quizDataRaw = route.queryParamMap.get('quizData');
  const quizData = decodeQuizDataParam(quizDataRaw);
  const onboardingData = toOnboardingQuiz(quizData);

  if (accessCode) {
    try {
      await firstValueFrom(authService.loginWithAccessCode(accessCode));
      await onboardingService.saveOnboardingQuiz(onboardingData);

      const consentOk = (await userInfoService.getUserChatPermission()) === true;

      const cleaned = stripAccessCode(state.url);
      if (consentOk) {
        return router.parseUrl(cleaned || '/health-assistant');
      } else {
        return router.createUrlTree(
          ['/consent'],
          { queryParams: { redirect: '/health-assistant' } }
        );
      }
    } catch (e) {
      console.error('Access code login failed', e);
      return router.createUrlTree(['/login'], { queryParams: { redirect: state.url } });
    }
  }

  if (!authService.isLoggedIn && refreshToken) {
    try {
      await firstValueFrom(authService.signWithRefreshToken(refreshToken));
      return true;
    } catch (error) {
      console.error('Refresh login failed', error);
      return router.createUrlTree(['/login']);
    }
  }

  if (!refreshToken) return router.createUrlTree(['/home']);
  if (!authService.isLoggedIn) return router.createUrlTree(['/home']);

  return true;
};

export const consentEntryGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const permissions = inject(UserInfoService);

  const isLoggedIn = typeof auth.isLoggedIn === 'function'
    ? await Promise.resolve(auth.isLoggedIn)
    : !!auth.isLoggedIn;

  if (!isLoggedIn) {
    return router.createUrlTree(['/login'], { queryParams: { redirect: state.url } });
  }

  const consentOk = (await permissions.getUserChatPermission()) === true;

  if (consentOk) {
    const redirect = route.queryParamMap.get('redirect') || '/health-assistant';
    return router.parseUrl(redirect);
  }

  return true;
};

export const redirectIfAuthedGuard: CanActivateFn = async (_route, _state): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const permissions = inject(UserInfoService);

  const isLoggedIn = typeof auth.isLoggedIn === 'function'
    ? await Promise.resolve(auth.isLoggedIn)
    : !!auth.isLoggedIn;

  if (!isLoggedIn) return true;

  const consentOk = (await permissions.getUserChatPermission()) === true;

  return consentOk
    ? router.createUrlTree(['/health-assistant'])
    : router.createUrlTree(['/consent'], { queryParams: { redirect: '/health-assistant' } });
};

export const consentGuard: CanActivateFn = async (_route, state): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const perms = inject(UserInfoService);
  const auth = inject(AuthService);

  const isLoggedIn = auth.isLoggedIn;
  if (!isLoggedIn) {
    return router.createUrlTree(['/login'], { queryParams: { redirect: state.url } });
  }

  const ok = (await perms.getUserChatPermission()) === true;
  return ok ? true : router.createUrlTree(['/consent'], { queryParams: { redirect: state.url } });
};
